import React, { useState, useEffect } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Input } from './ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { ScrollArea } from './ui/scroll-area';
import { Search, Loader2 } from 'lucide-react';
import { userAPI } from '../lib/api';
import { useTranslation } from '../context/LanguageContext';
import { getAvatarFallback } from '../lib/utils';

export function UserSearchDialog({ open, onOpenChange, onSelect }) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!query || query.length < 2) {
      setResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await userAPI.search(query);
        setResults(res.data.users || []);
      } catch (err) {
        // ignore
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setResults([]);
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('messages.newMessage')}</DialogTitle>
        </DialogHeader>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('component.searchUsers')}
            className="pl-10"
            autoFocus
          />
        </div>
        <ScrollArea className="max-h-60">
          {loading ? (
            <div className="flex justify-center py-6">
              <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
            </div>
          ) : results.length > 0 ? (
            <div className="space-y-1">
              {results.map((u) => (
                <button
                  key={u.user_id}
                  className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors text-left"
                  onClick={() => {
                    onSelect(u);
                    onOpenChange(false);
                  }}
                >
                  <Avatar className="w-10 h-10">
                    <AvatarImage src={u.picture} alt={u.name} />
                    <AvatarFallback className="bg-orange-100 text-orange-700 text-sm">
                      {getAvatarFallback(u.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium text-slate-900 text-sm">{u.name}</p>
                    <p className="text-xs text-slate-500">{u.email}</p>
                  </div>
                </button>
              ))}
            </div>
          ) : query.length >= 2 ? (
            <p className="text-center text-slate-500 py-6 text-sm">{t('component.noUsersFound')}</p>
          ) : (
            <p className="text-center text-slate-400 py-6 text-sm">Type at least 2 characters to search</p>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
